import resolveResponse from 'contentful-resolve-response'

function clone<T>(data: T): T {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(data)
  }

  return JSON.parse(JSON.stringify(data))
}

export type SafeResolveResponseOptions = {
  /**
   * When enabled, unresolved entries remain in `fields` after resolution.
   * When disabled (the default), unresolved entries are removed from `fields` after resolution.
   *
   * This is useful for incrementally migrating pages to the new behavior, or for pages whose
   * payload structure is not yet compatible with unresolved entry removal.
   *
   * @default false
   */
  legacyMode?: boolean
}

/**
 * Resolves Contentful response links and removes unresolved entries from `fields`,
 * without mutating the original input.
 *
 * @param json The JSON object to resolve.
 * @returns The resolved JSON object.
 */
export function safeResolveResponse(json: unknown, options?: SafeResolveResponseOptions): unknown {
  if (options?.legacyMode) {
    // In legacy mode, we use a backwards-compatible behavior that doesn't remove unresolved entries.
    return resolveResponse(json)
  }

  /**
   * Deep clone to avoid mutations from `resolveResponse`, which modifies its input in place.
   *
   * This is especially important in React components where re-renders may call this repeatedly
   * with the same object.
   */
  const cloned = clone(json)

  return resolveResponse(cloned, {removeUnresolved: true, itemEntryPoints: ['fields']})
}
